/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-08-04 14:15:11
 **/

import { useCallback, useMemo } from "react";

import { Maybe } from "@/frameworks/libraries/components";

import { usePhoneCall } from "@/frameworks/core/hooks";

import SingleCallMediaLayout from "./SingleCallMediaLayout";
import GroupCallMediaLayout from "./GroupCallMediaLayout";
import GroupCallHistoryMediaLayout from "./GroupCallHistoryMediaLayout";
import { removeMeFromList } from "@/frameworks/core/utils";

const LOGTAG = "#MediaLayout#";

export default function MediaLayout(props: PushServices.Call.PhoneDetailProps) {
	const phoneCall = usePhoneCall();
	const callID = phoneCall.state.id[0];
	const [callees, setCallees] = phoneCall.state.callees;
	const callUsers = phoneCall.state.users(callID)[0];
	const detailInfo = phoneCall.state.phone_detail[0];
	const isGroupCallHistory = detailInfo.tabID === "group-call-history";

	const handleUIType = useCallback(() => {
		if (phoneCall.status(callID) !== "ONGOING" || !callUsers) {
			return "AVATARONLY";
		}
		if (callUsers) {
			const usersWithVideoOn = Object.values(callUsers).filter((user) => user.media?.video === "on");

			const userWithScreenOn = Object.values(callUsers).filter((user) => user.media?.screenShare);

			if (userWithScreenOn.length > 0) {
				return "SCREENSHARE";
			}

			if (usersWithVideoOn.length === 0) return "AVATARONLY";

			if (usersWithVideoOn.length >= 1) {
				return "VIDEOCALL";
			}
		}
		return "AVATARONLY";
	}, [callUsers]);

	const handleGroupCallUIType = useCallback(() => {
		if (phoneCall.status(callID) !== "ONGOING" || !callID) {
			return "AVATARONLY";
		}
		if (callUsers) {
			const usersWithVideoOn = Object.values(callUsers).filter((user) => user.media?.video === "on");

			const userWithScreenOn = Object.values(callUsers).filter((user) => user.media?.screenShare);

			// if (usersWithVideoOn.length === 1 || (usersWithVideoOn.length === 2 && userWithScreenOn.length === 0)) {
			// 	return "VIDEOCALL_WITH_TWO_USER";
			// }
			if (usersWithVideoOn.length === 0 && userWithScreenOn.length === 0) return "AVATARONLY";
		}
		return "GRID_UI";
	}, [callUsers]);

	const isScreenShared = useCallback(() => {
		if (!callUsers) return false;
		return Object.values(callUsers).filter((user) => user.media?.screenShare).length > 0;
	}, [callUsers]);

	const userList = phoneCall.state.participatedUserList(callID);

	const participatedUserIDList = useMemo(() => {
		return userList.length > 0 ? removeMeFromList(userList) : props.callees.map((user) => user.userID);
	}, [callID, userList]);

	return (
		<Maybe
			test={isGroupCallHistory && !callID}
			fallback={
				<Maybe
					test={participatedUserIDList.length === 1}
					fallback={
						<GroupCallMediaLayout
							removeUser={setCallees}
							callID={callID}
							callAddUserList={participatedUserIDList}
							layout={handleGroupCallUIType()}
							isScreenShare={isScreenShared()}
						/>
					}
				>
					<SingleCallMediaLayout callees={participatedUserIDList} layout={handleUIType()} callID={callID} />
				</Maybe>
			}
		>
			<GroupCallHistoryMediaLayout {...props} />
		</Maybe>
	);
}
